import { Subtract, Tuple } from "./typeUtils";

// Define the icons we're using here:
const knownSensorIcons = ["quiet", "lively", "busy", "full"] as const;
const unknownSensorIcon = "unknown";

// Initial configuration is defined as the default.
const DEFAULT_BUCKETS: SensorIconBucket[] = [
  {
    upToAndIncludingCapacity: Number.MAX_SAFE_INTEGER,
    breakpoints: [0.2, 0.56, 0.96],
    tentativeToPresenceMultiplier: 0.5,
    unknownAllowedRatio: 0.5,
  },
];

// These ensure typesafety in our iconForSensorData function
// - All possible output of the function
export type SensorIcon =
  | (typeof knownSensorIcons)[number]
  | typeof unknownSensorIcon;

// - Ensure the number of breakpoints is correct
type Breakpoints = Tuple<
  Subtract<(typeof knownSensorIcons)["length"], 1>,
  number
>;

// These are intentionally different from SensorMarkerData; we don't want to
// create a dependency on another project in the map module
type SensorData = {
  capacity: number;
  nrPresent: number;
  nrTentative: number;
  nrUnknown: number;
};

export type SensorIconBucket = {
  upToAndIncludingCapacity: number;
  tentativeToPresenceMultiplier: number;
  unknownAllowedRatio: number;

  breakpoints: Breakpoints;
};

export const iconForSensorData = (
  sensorData: SensorData,
  buckets = DEFAULT_BUCKETS
): SensorIcon => {
  const relevantBucket = buckets.find(
    (b) => sensorData.capacity <= b.upToAndIncludingCapacity
  );
  if (!relevantBucket) {
    throw new RangeError(
      `No bucket provided that can contain capacity of ${sensorData.capacity}`
    );
  }

  const unknownPercentage = sensorData.nrUnknown / sensorData.capacity;
  if (
    isNaN(unknownPercentage) ||
    unknownPercentage >= relevantBucket.unknownAllowedRatio
  ) {
    return "unknown";
  }

  // Note: technically, we shouldn't use these for individual desks
  if (sensorData.capacity === 1) {
    if (sensorData.nrUnknown > 0) return "unknown";
    if (sensorData.nrPresent > 0) return "full";
    if (sensorData.nrTentative > 0) return "busy";
    return "quiet";
  }

  const occupiedPercentage = Math.min(
    1,
    (sensorData.nrPresent +
      sensorData.nrTentative * relevantBucket.tentativeToPresenceMultiplier) /
      sensorData.capacity
  );

  const idx = relevantBucket.breakpoints.findIndex(
    (bp) => occupiedPercentage <= bp
  );

  return knownSensorIcons[idx] || knownSensorIcons.at(-1);
};
